import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, ArrowLeft, User, Calendar, MapPin, Sparkles, GraduationCap, Briefcase,
  Heart, ShieldCheck, Lock, Download, FileText, Loader2, Star, Share2, Copy,
} from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import api, { authStore } from "../lib/api";

/**
 * /profile — the User's Profile page.
 *
 * The ONLY place where the Kundali PDF can be viewed/downloaded, gated behind
 * the Razorpay Checkout SDK. Home hub only lets users EDIT birth details.
 */

// Loose helper to ensure Razorpay Checkout script (added in public/index.html)
// has loaded before we open it.
const _waitForRzp = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve(window.Razorpay);
  let n = 0;
  const t = setInterval(() => {
    if (window.Razorpay) { clearInterval(t); resolve(window.Razorpay); }
    if (++n > 50) { clearInterval(t); reject(new Error("Razorpay SDK failed to load")); }
  }, 100);
});

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2.5">
    <Icon size={14} style={{ color: "var(--ash)", marginTop: 3 }}/>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>{label}</p>
      <p className="mt-0.5 text-sm" style={{ color: "var(--ink)" }}>{value || "—"}</p>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="editorial-card p-6">
    <p className="eyebrow mb-3">{title}</p>
    {children}
  </section>
);

export default function Profile() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [reg, setReg] = useState({});
  const [kundali, setKundali] = useState({ has_cache: false, is_unlocked: false, price_inr: 49 });
  const [busy, setBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [marriageBudget, setMarriageBudget] = useState("");
  const [budgetBusy, setBudgetBusy] = useState(false);
  const [budgetTier, setBudgetTier] = useState(null);
  const [budgetPrice, setBudgetPrice] = useState(null);

  useEffect(() => {
    if (!authStore.token()) { nav("/"); return; }
    (async () => {
      try {
        const [meR, kR] = await Promise.all([
          api.get("/auth/me"),
          api.get("/kundali/status"),
        ]);
        setMe(meR.data.user);
        setReg(meR.data.registration || {});
        setKundali(kR.data);
        const mb = meR.data.user?.marriage_budget
          ?? meR.data.registration?.family?.marriage_budget
          ?? "";
        setMarriageBudget(mb === null || mb === undefined ? "" : String(mb));
      } catch { toast.error("Failed to load profile"); }
    })();
  }, [nav]);

  const logout = () => { authStore.clear(); nav("/"); };

  const saveMarriageBudget = async () => {
    const n = parseInt(marriageBudget, 10);
    if (isNaN(n) || n < 0) { toast.error("Enter a valid budget in INR"); return; }
    setBudgetBusy(true);
    try {
      const { data } = await api.patch("/wizard/marriage-budget", { marriage_budget: n });
      setBudgetTier(data.derived_tier);
      setBudgetPrice(data.match_box_price);
      toast.success(`Saved · Match-Box tier auto-set to "${data.derived_tier}" (₹${data.match_box_price})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBudgetBusy(false); }
  };

  const shareKundali = async () => {
    setShareBusy(true);
    try {
      const { data } = await api.post("/kundali/share");
      const url = `${window.location.origin}/api/kundali/shared/${data.share_token}`;
      try {
        if (navigator.share) {
          await navigator.share({ title: "My Vedic Kundali", text: "View my authoritative Kundali", url });
          toast.success("Shared");
        } else {
          await navigator.clipboard.writeText(url);
          toast.success("Share link copied — valid 24 hours");
        }
      } catch { /* user dismissed */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Share failed");
    } finally { setShareBusy(false); }
  };

  const downloadKundali = async () => {
    setBusy(true);
    try {
      // Ensure a Kundali exists in the cache (auto-generate the first time).
      if (!kundali.has_cache) {
        toast.info("Fetching your Kundali from Prokerala…");
        await api.post("/kundali/generate", { ayanamsa: "lahiri" });
      }
      const resp = await api.get("/kundali/pdf", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const name = (reg.personal?.name || me?.mobile || "kundali").replace(/[^A-Za-z0-9_-]/g, "_");
      link.href = url;
      link.download = `kundali-${name}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Kundali downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    } finally { setBusy(false); }
  };

  const startPayment = async () => {
    if (!me?.id) return;
    setPayBusy(true);
    try {
      // 1) Create order server-side
      const { data: order } = await api.post("/payments/create-order", {
        target_profile_id: me.id,      // self-unlock for own Kundali
        type: "kundli",
      });

      // Already-unlocked idempotency shortcut
      if (order.already_unlocked) {
        toast.success("Kundali already unlocked");
        setKundali((k) => ({ ...k, is_unlocked: true }));
        return;
      }

      // Mock gateway fallback (test-preview without SDK keys)
      if (order.gateway === "mock" || !order.key_id) {
        toast.info("Mock payment mode — auto-verifying");
        setKundali((k) => ({ ...k, is_unlocked: true }));
        return;
      }

      // 2) Open Razorpay Checkout
      const Razorpay = await _waitForRzp();
      const options = {
        key: order.key_id,
        amount: order.amount * 100,
        currency: order.currency,
        order_id: order.order_id,
        name: "Vedika Sanskritik Bandhan",
        description: "Kundali PDF Unlock",
        prefill: { contact: me.mobile || "" },
        theme: { color: "#7A0E18" },
        // 3) Client-side handler → server verify
        handler: async (response) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment verified · Kundali unlocked");
            setKundali((k) => ({ ...k, is_unlocked: true }));
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      };
      const rzp = new Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Payment initiation failed");
    } finally { setPayBusy(false); }
  };

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--ivory)" }}>
        <p className="eyebrow inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Loading profile…</p>
      </div>
    );
  }

  const p = reg.personal || {};
  const r = reg.religion || {};
  const f = reg.family || {};
  const pc = reg.profile_creator || {};
  const a = reg.astrology || {};
  const e = reg.education || {};

  return (
    <div data-testid="profile-page" className="min-h-screen w-full flex flex-col" style={{ background: "var(--ivory)" }}>
      <header className="px-6 md:px-10 pt-6 pb-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--line)" }}>
        <BrandLogo size="sm"/>
        <div className="flex items-center gap-4">
          <button onClick={() => nav("/home")} data-testid="profile-back"
            className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--ash)" }}>
            <ArrowLeft size={14}/> Home
          </button>
          <button onClick={logout} data-testid="profile-logout"
            className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--ash)" }}>
            <LogOut size={14}/> Logout
          </button>
        </div>
      </header>

      <section className="px-6 md:px-10 pt-10 pb-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow flex items-center gap-2"><Sparkles size={12}/> My Profile</div>
            <h1 data-testid="profile-name" className="font-serif text-3xl md:text-4xl mt-2" style={{ color: "var(--crimson)" }}>
              {p.name || "Complete your profile"}
            </h1>
            <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>{me.mobile}</p>
          </div>
          <div className="flex items-center gap-2">
            {me.identity_verified || me.aadhaar_verified ? (
              <span data-testid="profile-okyc-verified" className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1"
                style={{ background: "rgba(85,127,74,0.10)", color: "var(--leaf)", border: "1px solid var(--leaf)" }}>
                <ShieldCheck size={11}/> Aadhaar Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1"
                style={{ background: "rgba(122,14,24,0.06)", color: "var(--crimson)", border: "1px solid var(--crimson)" }}>
                <Lock size={11}/> Aadhaar OKYC pending
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-10 pb-16 flex-1">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          <Section title="Personal">
            <Row icon={User}     label="Gender"     value={p.gender}/>
            <Row icon={Calendar} label="Date of birth" value={p.dob}/>
            <Row icon={Heart}    label="Marital status" value={p.marital_status}/>
            <Row icon={User}     label="Mother tongue"  value={p.mother_tongue}/>
          </Section>

          <Section title="Religion & Gotra">
            <Row icon={Star} label="Religion" value={r.religion || "Hindu"}/>
            <Row icon={Star} label="Caste"    value={r.caste}/>
            <Row icon={Star} label="Sub-caste"value={r.sub_caste}/>
            <Row icon={Star} label="Gotra"    value={r.gotra}/>
            <Row icon={Star} label="Manglik"  value={r.manglik || a.manglik}/>
          </Section>

          <Section title="Family">
            <Row icon={User} label="Father"      value={f.father_name}/>
            <Row icon={User} label="Mother"      value={f.mother_name}/>
            <Row icon={User} label="Siblings"    value={f.siblings}/>
            <Row icon={User} label="Family type" value={f.family_type}/>
            <Row icon={User} label="Diet"        value={f.diet}/>
          </Section>

          <Section title="Location & Creator">
            <Row icon={User}   label="Created by"     value={pc.profile_creator_relation}/>
            <Row icon={MapPin} label="City"           value={pc.city}/>
            <Row icon={MapPin} label="State"          value={pc.state}/>
            <Row icon={MapPin} label="Native village" value={pc.native_village}/>
          </Section>

          <Section title="Astrology">
            <Row icon={Star} label="Rashi"        value={a.rashi}/>
            <Row icon={Star} label="Nakshatra"    value={a.nakshatra}/>
            <Row icon={Star} label="Birth time"   value={a.birth_time}/>
            <Row icon={Star} label="Birth place"  value={a.birth_place}/>
          </Section>

          <Section title="Career & About">
            <Row icon={GraduationCap} label="Education"   value={e.education}/>
            <Row icon={Briefcase}     label="Profession"  value={e.profession}/>
            <Row icon={Briefcase}     label="Annual income" value={e.annual_income}/>
            <Row icon={Sparkles}      label="About"       value={e.about}/>
          </Section>

          {/* Marriage Budget · drives Match-Box tier (contract §5) */}
          <div data-testid="marriage-budget-card" className="editorial-card p-6 md:col-span-2"
            style={{ background: "var(--bone)", border: "1px solid var(--gold)" }}>
            <div className="flex items-start gap-3 mb-4">
              <Briefcase size={18} style={{ color: "var(--crimson)", marginTop: 2 }}/>
              <div>
                <p className="font-serif text-xl" style={{ color: "var(--crimson)" }}>Marriage Budget</p>
                <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>
                  Your budget (INR) auto-selects the Match-Box tier for anyone who wants to unlock your contact.
                  &lt; ₹5L → Everyday ₹250 · ₹5L–15L → Preferred ₹1,100 · &gt; ₹15L → Premium ₹3,100.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
              <div className="flex-1 w-full">
                <label className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Amount in INR</label>
                <input
                  data-testid="marriage-budget-input"
                  type="number"
                  min="0"
                  step="10000"
                  value={marriageBudget}
                  onChange={(e) => setMarriageBudget(e.target.value)}
                  placeholder="e.g., 750000"
                  className="underline-input w-full mt-1"
                />
              </div>
              <button data-testid="marriage-budget-save" onClick={saveMarriageBudget} disabled={budgetBusy} className="btn-primary">
                {budgetBusy ? "Saving…" : "Save Budget"}
              </button>
            </div>
            {(budgetTier || budgetPrice) && (
              <p data-testid="marriage-budget-derived" className="mt-4 text-[11px]" style={{ color: "var(--ash)" }}>
                Derived tier: <strong style={{ color: "var(--ink)" }}>{budgetTier}</strong> · Match-Box price:{" "}
                <strong style={{ color: "var(--crimson)" }}>₹{budgetPrice}</strong>
              </p>
            )}
          </div>

          {/* Kundali paywall — the ONLY place we allow view/download. */}
          <div data-testid="kundali-paywall-card" className="editorial-card p-6 md:col-span-2"
            style={{ background: "var(--bone)", border: "1px solid var(--gold)" }}>
            <div className="flex items-start gap-3 mb-4">
              <FileText size={18} style={{ color: "var(--crimson)", marginTop: 2 }}/>
              <div>
                <p className="font-serif text-xl" style={{ color: "var(--crimson)" }}>Detailed Kundali PDF</p>
                <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>
                  Vedic Janma Kundali generated live via Prokerala from your saved birth details.
                  {kundali.cached_at ? ` · Last refreshed ${new Date(kundali.cached_at).toLocaleDateString()}` : ""}
                </p>
              </div>
            </div>

            {kundali.is_unlocked ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <span data-testid="kundali-unlocked-pill" className="inline-flex items-center gap-1 text-[11px] tracking-widest uppercase"
                  style={{ color: "var(--leaf)" }}>
                  <ShieldCheck size={12}/> Unlocked · Download unlimited
                </span>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="kundali-share" onClick={shareKundali} disabled={shareBusy}
                    className="text-[11px] uppercase tracking-widest inline-flex items-center gap-1.5 px-3 py-2"
                    style={{ color: "var(--crimson)", border: "1px solid var(--crimson)" }}>
                    {shareBusy ? "Preparing…" : <><Share2 size={12}/> Share Kundali</>}
                  </button>
                  <button data-testid="kundali-download" onClick={downloadKundali} disabled={busy} className="btn-primary">
                    {busy ? "Preparing…" : "Download PDF"} <Download size={14}/>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <p className="font-serif text-2xl" style={{ color: "var(--ink)" }}>₹{kundali.price_inr || 49}</p>
                  <p className="mt-0.5 text-[11px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>
                    One-time · lifetime download access
                  </p>
                </div>
                <button data-testid="kundali-unlock" onClick={startPayment} disabled={payBusy} className="btn-primary">
                  {payBusy ? "Opening Checkout…" : "Unlock via Razorpay"} <Lock size={14}/>
                </button>
              </div>
            )}
            <p className="mt-4 text-[10px]" style={{ color: "var(--ash)" }}>
              Update your birth details from the Home hub&apos;s <em>Update Kundali Details</em> action before unlocking.
              Razorpay is currently in Test Mode — no real charge.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
